/**
 * Export utilities for Risk Reports
 * Supports PDF, Word document, and plain text exports
 */

import axios from '@/api/axios'

/**
 * Clean HTML content and extract plain text
 */
export function cleanHtmlContent(htmlContent) {
  if (!htmlContent) return ''
  
  // Keep paragraph and heading breaks: turn block-level tags into line breaks before extracting the text
  const withBreaks = String(htmlContent)
    .replace(/<\s*br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|h[1-6]|li|ul|ol|tr|table|blockquote)>/gi, '\n')

  // Create a temporary div to parse HTML
  const div = document.createElement('div')
  div.innerHTML = withBreaks
  const text = div.textContent || div.innerText || ''
  
  return text
    .replace(/[ \t]+/g, ' ') // Normalize spaces, keep line breaks
    .replace(/ ?\n ?/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

/**
 * Format footnotes for plain text
 */
export function formatFootnotesText(footnotes) {
  if (!footnotes || footnotes.length === 0) return ''
  
  let footnotesText = '\n\n--- References ---\n\n'
  
  footnotes.forEach((footnote, index) => {
    footnotesText += `${footnote.number || (index + 1)}. ${footnote.citation}`
    // The citation text already contains the link; only add it when it does not
    if (footnote.url && !String(footnote.citation || '').includes(footnote.url)) {
      footnotesText += ` (${footnote.url})`
    }
    const tierText = { primary: 'primary source', company: 'company-published', reputable: 'reputable outlet', database: 'business database', other: 'other source' }[footnote.sourceTierLabel]
    if (tierText) {
      footnotesText += ` [${tierText}]`
    }
    footnotesText += '\n\n'
  })
  
  return footnotesText
}

/**
 * Export report as plain text
 */
export function exportAsText(report) {
  if (!report) {
    throw new Error('Report data is required')
  }
  
  let content = ''
  
  // Header
  content += `${report.entityName} - Risk Analysis Report\n`
  content += `${'='.repeat(50)}\n\n`
  
  // Metadata
  content += `Report Type: ${report.reportType || 'Unknown'}\n`
  content += `Generated: ${new Date(report.metadata?.generatedAt || Date.now()).toLocaleDateString()}\n`
  if (report.quality?.overallScore) {
    content += `Quality Score: ${report.quality.overallScore}/100 (${report.quality.rating})\n`
  }
  content += '\n'
  
  // Executive Summary
  if (report.content?.executiveSummary) {
    content += 'EXECUTIVE SUMMARY\n'
    content += '-'.repeat(20) + '\n'
    content += cleanHtmlContent(report.content.executiveSummary) + '\n\n'
  }
  
  // Report Sections
  if (report.content?.sections) {
    report.content.sections.forEach((section, index) => {
      content += `${section.title.toUpperCase()}\n`
      content += '-'.repeat(section.title.length) + '\n'
      content += cleanHtmlContent(section.content) + '\n\n'
    })
  }
  
  // Full Report (if sections not available)
  if (!report.content?.sections && report.content?.fullReport) {
    content += 'FULL REPORT\n'
    content += '-'.repeat(15) + '\n'
    content += cleanHtmlContent(report.content.fullReport) + '\n\n'
  }
  
  // Footnotes
  if (report.content?.footnotes) {
    content += formatFootnotesText(report.content.footnotes)
  }
  
  return content
}

/**
 * Export report as PDF via API
 */
export async function exportAsPDF(report, options = {}) {
  if (!report) {
    throw new Error('Report data is required')
  }
  console.log('Exporting report as PDF:', report);
  try {
    const response = await axios.get(`/risk-reports/projects/${options.projectId}/sessions/${options.sessionId}/pdf`, {
      responseType: 'blob'
    })
    
    // Extract filename from Content-Disposition header or use default
    const contentDisposition = response.headers['content-disposition']
    const filename = contentDisposition?.match(/filename="(.+)"/)?.[1] || 
                    generateFilename(report, 'pdf', options)
    
    downloadFile(response.data, filename, 'application/pdf')
    
    return { success: true, filename }
  } catch (error) {
    console.error('PDF export error:', error)
    throw new Error(`Failed to export PDF: ${error.response?.data?.error || error.message}`)
  }
}

/**
 * Export report as Word document via API
 */
export async function exportAsWord(report, options = {}) {
  if (!report) {
    throw new Error('Report data is required')
  }
  
  try {
    const response = await axios.get(`/risk-reports/projects/${options.projectId}/sessions/${options.sessionId}/word`, {
      responseType: 'blob'
    })
    
    // Extract filename from Content-Disposition header or use default
    const contentDisposition = response.headers['content-disposition']
    const filename = contentDisposition?.match(/filename="(.+)"/)?.[1] || 
                    generateFilename(report, 'docx', options)
    
    downloadFile(response.data, filename, 'application/vnd.openxmlformats-officedocument.wordprocessingml.document')
    
    return { success: true, filename }
  } catch (error) {
    console.error('Word export error:', error)
    throw new Error(`Failed to export Word document: ${error.response?.data?.error || error.message}`)
  }
}

/**
 * Download file with given content and filename
 */
export function downloadFile(content, filename, mimeType = 'text/plain') {
  const blob = new Blob([content], { type: mimeType })
  const url = URL.createObjectURL(blob)
  
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.style.display = 'none'
  
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  
  URL.revokeObjectURL(url)
}

/**
 * Copy text to clipboard
 */
export async function copyToClipboard(text) {
  if (!navigator.clipboard) {
    // Fallback for older browsers
    const textArea = document.createElement('textarea')
    textArea.value = text
    textArea.style.position = 'fixed'
    textArea.style.left = '-999999px'
    textArea.style.top = '-999999px'
    document.body.appendChild(textArea)
    textArea.focus()
    textArea.select()
    
    try {
      const successful = document.execCommand('copy')
      document.body.removeChild(textArea)
      return successful
    } catch (err) {
      document.body.removeChild(textArea)
      throw new Error('Failed to copy to clipboard')
    }
  }
  
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch (err) {
    throw new Error('Failed to copy to clipboard')
  }
}

/**
 * Generate filename for exports
 */
export function generateFilename(report, extension, options = {}) {
  if (!report) return `risk-report.${extension}`
  
  const entityName = report.entityName?.replace(/[^a-zA-Z0-9]/g, '_') || 'unknown'
  const reportType = report.reportType?.replace(/[^a-zA-Z0-9]/g, '_') || 'comprehensive'
  const date = new Date().toISOString().split('T')[0] // YYYY-MM-DD
  
  const prefix = options.prefix || 'risk_report'
  
  return `${prefix}_${entityName}_${reportType}_${date}.${extension}`
}

/**
 * Main export function that handles all formats
 */
export async function exportReport(report, format, options = {}) {
  if (!report) {
    throw new Error('Report data is required for export')
  }
  
  const filename = generateFilename(report, format, options)
  
  try {
    switch (format) {
      case 'txt': {
        const content = exportAsText(report)
        downloadFile(content, filename, 'text/plain')
        break
      }
      
      case 'pdf': {
        await exportAsPDF(report, options)
        break
      }
      
      case 'docx': {
        await exportAsWord(report, options)
        break
      }
      
      case 'copy': {
        const content = exportAsText(report)
        await copyToClipboard(content)
        break
      }
      
      default:
        throw new Error(`Unsupported export format: ${format}`)
    }
    
    return { success: true, filename }
  } catch (error) {
    console.error('Export failed:', error)
    throw error
  }
}